import React, { useEffect, useState, Fragment } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'
import { bindActionCreators } from 'redux'
import { ToastContainer } from 'react-toastify'
import Select from 'react-select'

import { colourStyles } from 'utils/selectStyle'
import { usePrevProp } from 'utils/hooks'
import screenType from 'component/menuMission/screenType'
import Col from 'component/utils/Col'
import ContentHeader from 'component/utils/ContentHeader'
import Grid from 'component/utils/Grid'
import Notification from 'component/utils/Notification'
import Options from 'component/menuMission/Options'
import Pagination from 'component/pagination/Pagination'
import SideFilterForm from 'component/menuMission/SideFilterForm'
import Table from 'component/menuMission/Table'
import UpperFilterForm from 'component/menuMission/UpperFilterForm'

import 'styles/overwrittenAndNew/newPageStyle.css'

// Modals
import DuplicatedModal from 'component/menuMission/DuplicatedModal'
import EditModal from 'component/menuMission/EditModal'
import RemovalModal from 'component/Modal/ExclusionModal'
import SuccessModal from 'component/Modal/SuccessModal'
import UpdatePermissionsModal from 'component/menuMission/UpdatePermissionsModal'
import ViewModal from 'component/menuMission/ViewModal'
import WarningModal from 'component/menuMission/WarningModal'

import * as menuActions from 'action/menu'
import * as menuMissionActions from 'action/menuMission'
import * as missionsActions from 'action/missions'

/**
 * TIP_SHOW of menus
 */
const APP_PERMISSION = 1
const WEB_PERMISSION = 2

/**
 * Control which API should be called
 */
const SHOULD_SAVE = 1
const SHOULD_UPDATE = 2

const DEFAULT_FILTER_INITIAL_VALUES = {
  genericSearch: '',
  isMission: true,
  isPersonalProfile: true,
  lsCdMenus: [],
}

const DEFAULT_EDIT_INITIAL_VALUES = {
  cdMission: '',
  dsLdap: '',
  dsProfile: '',
  lsCdMenus: [],
  nmMissions: '',
  type: screenType.CREATE_PERSONAL_PROFILE_TYPE,
}

const PAGINATION_OPTIONS = [
  { label: 10, value: 10 },
  { label: 20, value: 20 },
  { label: 30, value: 30 },
  { label: 40, value: 40 },
  { label: 50, value: 50 },
]

const DEFAULT_PAGINATION = {
  hasPagination: true,
  page: 1,
  size: 10,
}

const MenuMissionContainer = ({
  all,
  auth: {
    user: { dsToken },
  },
  count,
  current,
  deleteProfile,
  exportCsv,
  fetch,
  fetchMissions,
  fetchPermissions,
  missions,
  pages,
  permissions,
  records = [],
  save,
  translate,
  updatePermissions,
  updateProfile,
}) => {
  const [openEditModal, setOpenEditModal] = useState(false)
  const [openDuplicatedModal, setOpenDuplicatedModal] = useState(null)
  const [openRemoveModal, setOpenRemoveModal] = useState(null)
  const [openSuccessModal, setOpenSuccessModal] = useState(null)
  const [openUpdatePermissionsModal, setOpenUpdatePermissionsModal] =
    useState(null)
  const [openViewModal, setOpenViewModal] = useState(null)
  const [openWarningModal, setOpenWarningModal] = useState(null)

  const [editInitialValues, setEditInitialValues] = useState(
    DEFAULT_EDIT_INITIAL_VALUES
  )
  const [shouldSaveOrUpdate, setShouldSaveOrUpdate] = useState(SHOULD_SAVE)

  const [lsDsProfiles, setLsDsProfiles] = useState([])

  const [filters, setFilters] = useState(DEFAULT_FILTER_INITIAL_VALUES)
  const prevFilters = usePrevProp(filters)
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION)
  const prevPagination = usePrevProp(pagination)
  const [sideFilterVisible, setSideFilterVisible] = useState(false)
  const [orderDirection, setOrderDirection] = useState('asc')
  const [modalBtnRef, setModalBtnRef] = useState(null)

  const [showWarning, setShowWarning] = useState(false)

  const [firstFetch, setFirstFetch] = useState(true)

  const [showTable, setShowTable] = useState(false)

  const webPermissions = permissions.filter(
    (p) => p.tipShow === WEB_PERMISSION && p.cdMenu !== 106
  )
  webPermissions.sort((a, b) => {
    if (a.dsMenuParent < b.dsMenuParent) return -1
    if (a.cdMenuParent > b.cdMenuParent) return 1
    return 0
  })

  const appPermissions = permissions.filter((p) => p.tipShow === APP_PERMISSION)
  appPermissions.sort((aa, bb) => {
    if (aa.dsMenuParent < bb.dsMenuParent) return -1
    if (aa.dsMenuParent > bb.dsMenuParent) return 1
    return 0
  })

  /**
   * Did mount
   */
  useEffect(() => {
    fetchPermissions(dsToken)

    fetchMissions(dsToken)

    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    handleFetch()
  }, [orderDirection])

  /**
   * When filters change
   */
  useEffect(() => {
    if (!firstFetch) {
      if (filters !== prevFilters) {
        if (pagination.page > 1) {
          setPagination({ ...pagination, page: 1 })
        } else {
          handleFetch()
        }
      }
    }
  }, [filters])

  /**
   * When pagination changes
   */
  useEffect(() => {
    if (!firstFetch) {
      if (pagination !== prevPagination) {
        handleFetch()
      }
    }
  }, [pagination])

  /**
   * Handle data fetch
   */
  const handleFetch = async (reset = false) => {
    handleResize()
    setFirstFetch(false)

    const param = {
      ...filters,
      ...pagination,
      order: 'dsProfile',
      orderDirection,
    }

    reset && (param.page = 1)

    setShowTable(false)

    await fetch(dsToken, param)

    setShowTable(true)
  }

  /**
   * Resize screen
   */
  const handleResize = () => {}

  /**
   * Set new filters
   */
  const setValues = (values) => {
    setFilters({ ...filters, ...values })
  }

  /**
   * Render pagination
   */
  const renderPagination = () => (
    <Col className='align-center margin-bottom margin-top-triple new-style-pagination'>
      {!!records.length && pages > 1 && showTable && (
        <Pagination
          count={count}
          current={current}
          getPage={(page) => setPagination({ ...pagination, page })}
          pages={pages}
        />
      )}

      {!!records.length &&
        pages > 1 &&
        showTable &&
        renderPaginationSizeSelect()}
    </Col>
  )

  /**
   * Render pagination size select
   */
  const renderPaginationSizeSelect = () => (
    <div className='pagination-size-selector'>
      <Col xs={6}>{translate('pagination.size.selector')}</Col>
      <Col xs={4}>
        <Select
          classNamePrefix='react-select'
          onChange={(option) =>
            setPagination({ ...pagination, page: 1, size: option.value })
          }
          options={PAGINATION_OPTIONS}
          styles={colourStyles}
          value={PAGINATION_OPTIONS.find((i) => i.value === pagination.size)}
        />
      </Col>
    </div>
  )

  /**
   * Render modal to create profile
   */
  const renderEditModal = () => {
    if (!openEditModal) return

    const handleOpenWarningModal = (values) => {
      setEditInitialValues(values)

      setOpenEditModal(false)

      setOpenWarningModal(true)

      setShowWarning(true)
    }


    return (
      <EditModal
        allPermissions={all}
        appPermissions={appPermissions}
        closeModal={() => setOpenEditModal(false)}
        handleOpenWarningModal={handleOpenWarningModal}
        initialValues={editInitialValues}
        missions={missions}
        submit={handleSave}
        translate={translate}
        webPermissions={webPermissions}
        token={dsToken}
        showWarning={showWarning}
      />
    )
  }

  /**
   * Render modal to remove profile
   */
  const renderRemoveModal = () => {
    const handleRemove = async () => {
      setOpenRemoveModal(null)

      await deleteProfile(dsToken, openRemoveModal, () =>
        setOpenSuccessModal(screenType.REMOVE_TYPE)
      )

      handleFetch(true)
    }

    return (
      <RemovalModal
        t={translate}
        title={translate('punctual.adj.approvers.remove.perm')}
        message={
          <>
            <br />
            {translate('missionMenu.removeModal.text')}
            <br />
          </>
        }
        confirmButtonMsg='approvers.remove.confirm'
        onConfirm={() => {
          handleRemove()
        }}
        renderButton={({ onClick }) => (
          <div onClick={onClick} ref={(input) => setModalBtnRef(input)} />
        )}
      />
    )
  }

  /**
   * Render success modal
   */
  const renderSuccessModal = () => {
    if (!openSuccessModal) return

    const handleCloseSuccessModal = () => {
      setLsDsProfiles([])

      setOpenSuccessModal(null)
    }

    return (
      <SuccessModal
        handleClose={handleCloseSuccessModal}
        t={translate}
        msg={translate(`missionMenu.successModal.text.${openSuccessModal}`)}
        size='md'
      />
    )
  }

  const renderUpdatePermissionsModal = () => {
    if (!openUpdatePermissionsModal) return

    const handleUpdatePermissions = async (values) => {
      setOpenUpdatePermissionsModal(null)

      setLsDsProfiles([])

      const finalValues =
        values.type === screenType.ADD_PERMISSIONS
          ? {
              toSave: {
                ...values,
                dsProfileList: lsDsProfiles.map((i) => i.dsProfile),
              },
            }
          : {
              toRemove: {
                ...values,
                dsProfileList: lsDsProfiles.map((i) => i.dsProfile),
              },
            }

      await updatePermissions(dsToken, finalValues, () =>
        setOpenSuccessModal(screenType.EDIT_PERSONAL_PROFILE_TYPE)
      )

      handleFetch()
    }

    return (
      <UpdatePermissionsModal
        appPermissions={appPermissions}
        closeModal={() => setOpenUpdatePermissionsModal(null)}
        initialValues={openUpdatePermissionsModal}
        lsIdProfilesLength={lsIdProfilesLength}
        submit={handleUpdatePermissions}
        translate={translate}
        webPermissions={webPermissions}
      />
    )
  }

  /**
   * Render modal to view info about a profile
   */
  const renderViewModal = () => {
    if (!openViewModal) return

    const handleEdit = () => {
      setOpenViewModal(null)

      setShouldSaveOrUpdate(SHOULD_UPDATE)

      const updateData = {
        ...openViewModal,
        selectedAppPermissions: openViewModal.lsCdMenus.filter((m) =>
          appPermissions.some((p) => p.cdMenu === m)
        ),
        selectedWebPermissions: openViewModal.lsCdMenus.filter((m) =>
          webPermissions.some((p) => p.cdMenu === m)
        ),
      }

      setEditInitialValues(updateData)

      setOpenEditModal(true)
    }

    return (
      <ViewModal
        closeModal={() => setOpenViewModal(null)}
        openEditModal={handleEdit}
        record={openViewModal}
        translate={translate}
      />
    )
  }

  /**
   * Render modal to warn after closing other modal
   */
  const renderWarningModal = () => {
    if (!openWarningModal) return

    const handleConfirm = () => {
      setEditInitialValues(DEFAULT_EDIT_INITIAL_VALUES)

      setShouldSaveOrUpdate(SHOULD_SAVE)

      setOpenWarningModal(false)

      setShowWarning(false)
    }

    const handleCancel = () => {
      setOpenWarningModal(false)

      setOpenEditModal(true)
    }

    return (
      <WarningModal
        handleCancel={handleCancel}
        handleConfirm={handleConfirm}
        translate={translate}
      />
    )
  }

  /**
   * Render duplicate personal profile modal
   */
  const renderDuplicatedModal = () => {
    if (!openDuplicatedModal) return

    const handleEdit = () => {
      setOpenDuplicatedModal(null)

      setShouldSaveOrUpdate(SHOULD_UPDATE)

      const updateData = {
        ...openDuplicatedModal,
        selectedAppPermissions: openDuplicatedModal.lsCdMenus.filter((m) =>
          appPermissions.some((p) => p.cdMenu === m)
        ),
        selectedWebPermissions: openDuplicatedModal.lsCdMenus.filter((m) =>
          webPermissions.some((p) => p.cdMenu === m)
        ),
        type: screenType.EDIT_PERSONAL_PROFILE_TYPE,
      }

      setEditInitialValues(updateData)

      setOpenEditModal(true)
    }

    const handleNew = () => {
      setOpenDuplicatedModal(null)

      setShouldSaveOrUpdate(SHOULD_SAVE)

      const newData = {
        ...editInitialValues,
        selectedAppPermissions: editInitialValues.lsCdMenus.filter((m) =>
          appPermissions.some((p) => p.cdMenu === m)
        ),
        selectedWebPermissions: editInitialValues.lsCdMenus.filter((m) =>
          webPermissions.some((p) => p.cdMenu === m)
        ),
        type: screenType.CREATE_PERSONAL_PROFILE_TYPE,
      }

      setEditInitialValues(newData)

      setOpenEditModal(true)
    }

    return (
      <DuplicatedModal
        handleEdit={handleEdit}
        handleNew={handleNew}
        info={openDuplicatedModal}
        translate={translate}
      />
    )
  }

  /**
   * Render side filter
   */
  const renderSideFilterForm = () => {
    if (!sideFilterVisible) return

    const handleSubmitSideFilterForm = (values) => {
      setSideFilterVisible(false)

      setValues(values)
    }

    return (
      <SideFilterForm
        appPermissions={appPermissions}
        initialValues={filters}
        setSideFilterVisible={toggleSideFilter}
        submit={handleSubmitSideFilterForm}
        translate={translate}
        webPermissions={webPermissions}
      />
    )
  }

  /**
   * Get values and manage modals while saving
   */
  const handleSave = async (values) => {
    setOpenEditModal(false)
    setShowWarning(false)

    if (shouldSaveOrUpdate === SHOULD_SAVE) {
      setEditInitialValues(values)
      if(values.dsProfile) {
        const regex = /(?:\\[rntvfb0"]|[\r\n\t\v\f\b\0]+)+/g;
        values.dsProfile = values.dsProfile.replace(regex,"").replace(/\s+/g, ' ').trim()
      }
      await save(
        dsToken,
        values,
        () => setOpenSuccessModal(values.type),
        setOpenDuplicatedModal
      )
    } else {
      await updateProfile(dsToken, values, () =>
        setOpenSuccessModal(screenType.EDIT_PERSONAL_PROFILE_TYPE)
      )
    }

    handleFetch()
  }

  /**
   * Toggle side filter
   */
  const toggleSideFilter = () => {
    setSideFilterVisible(!sideFilterVisible)
  }

  const handleOrderChange = () => {
    setOrderDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
  }

  /**
   * Render upper section with filter and buttons
   */
  const renderUpperSection = () => {
    /**
     * Hanle modal to edit permissions
     */
    const handleOpenEditModal = (values) => {
      setEditInitialValues({ ...DEFAULT_EDIT_INITIAL_VALUES, ...values })

      setOpenEditModal(true)

      setShouldSaveOrUpdate(SHOULD_SAVE)
    }

    /**
     * Render upper options
     */
    const renderOptions = () => {
      const list = [
        {
          label: 'delete',
          action: () => {
            setOpenRemoveModal(lsDsProfiles)
            modalBtnRef.click()
          },
        },
      ]

      if (lsIdProfilesLength > 1) {
        list.push({
          label: 'remove',
          action: () =>
            setOpenUpdatePermissionsModal({
              type: screenType.REMOVE_PERMISSIONS,
            }),
        })
        list.push({
          label: 'add',
          action: () =>
            setOpenUpdatePermissionsModal({ type: screenType.ADD_PERMISSIONS }),
        })
      }

      list.push({
        label: 'cancel',
        action: () => {
          setLsDsProfiles([])
          setValues({
            genericSearch: '',
          })
        },
      })

      return list.map(({ label, action }) => (
        <Fragment key={label}>
          {' '}
          |{' '}
          <span
            onClick={action}
            style={{ color: '#72BF44', cursor: 'pointer' }}
          >
            {translate(`missionMenu.selection.${label}`)}
          </span>
        </Fragment>
      ))
    }

    return lsIdProfilesLength ? (
      <div className='new-actions'>
        <strong>{`${lsIdProfilesLength} ${translate(
          `missionMenu.selection.quantity${
            lsIdProfilesLength > 1 ? 'Plural' : 'Singular'
          }`
        )}`}</strong>

        {renderOptions()}
      </div>
    ) : (
      <div className='new-style'>
        <Grid fluid className='new-upper-container'>
          <Col xs={12} md={5} lg={4}>
            <UpperFilterForm
              initialValues={filters}
              submit={setValues}
              translate={translate}
            />
          </Col>
          <Col className='mission-options-col' style={{ float: 'right', width: 'auto' }}>
            <Options
              setOpenEditModal={handleOpenEditModal}
              setSideFilterVisible={toggleSideFilter}
              onExport={() => exportCsv(dsToken, filters, translate)}
              translate={translate}
            />
          </Col>
        </Grid>
      </div>
    )
  }

  const lsIdProfilesLength = lsDsProfiles.length

  /**
   * Render data table
   */
  const renderTable = () => {
    const handleOpenViewModal = (record) => {
      const newValues = {
        ...record,
        type: record.dsLdap
          ? screenType.EDIT_PERSONAL_PROFILE_TYPE
          : screenType.EDIT_MISSION_PROFILE_TYPE,
      }

      setOpenViewModal(newValues)
    }

    const handleOpenEditModal = (record) => {
      setOpenViewModal(null)

      setShouldSaveOrUpdate(SHOULD_UPDATE)

      const updateData = {
        ...record,
        selectedAppPermissions: record.lsCdMenus.filter((m) =>
          appPermissions.some((p) => p.cdMenu === m)
        ),
        selectedWebPermissions: record.lsCdMenus.filter((m) =>
          webPermissions.some((p) => p.cdMenu === m)
        ),
        type: record.dsLdap
          ? screenType.EDIT_PERSONAL_PROFILE_TYPE
          : screenType.EDIT_MISSION_PROFILE_TYPE,
      }

      setEditInitialValues(updateData)

      setOpenEditModal(true)
    }

    return records.length ? (
      showTable ? (
        <Table
          handleResize={handleResize}
          lsDsProfiles={lsDsProfiles}
          openEditModal={handleOpenEditModal}
          openRemoveModal={(record) => setOpenRemoveModal([record])}
          openViewModal={handleOpenViewModal}
          handleProfileDeletion={(val) =>
            deleteProfile(dsToken, [val], () => {
              setOpenSuccessModal(screenType.REMOVE_TYPE)
              handleFetch(true)
            })
          }
          records={records}
          setLsDsProfiles={setLsDsProfiles}
          translate={translate}
          onOrderChange={handleOrderChange}
          orderBy={orderDirection === 'asc'}
        />
      ) : (
        ''
      )
    ) : (
      <Col
        xs={12}
        className='margin-top margin-right'
        style={{ marginLeft: `${sideFilterVisible ? 0.5 : 1}rem` }}
      >
        <Notification
          message={translate('no.data.found')}
          className='notification-warning'
        />
      </Col>
    )
  }

  return (
    <Fragment>
      <div className='new-style'>
        <ContentHeader>{translate('mobile.missions')}</ContentHeader>
        <div className='padding-bottom new-style-filter'>
          {renderUpperSection()}
        </div>
        <div className='new-style-table'>
          <Grid fluid className='no-padding'>
            <div className='outer-table new-outer-table'>
              <div>{renderTable()}</div>

              {renderPagination()}
            </div>
          </Grid>
        </div>

        {renderDuplicatedModal()}
        {renderEditModal()}
        {renderRemoveModal()}
        {renderSuccessModal()}
        {renderUpdatePermissionsModal()}
        {renderViewModal()}
        {renderWarningModal()}

        <ToastContainer newestOnTop />
      </div>

      {renderSideFilterForm()}
    </Fragment>
  )
}

/* istanbul ignore next */
const mapStateToProps = ({
  menuReducer,
  menuMissionReducer,
  missionsReducer,
  paginateReducer,
}) => ({
  count: paginateReducer.count,
  current: paginateReducer.current,
  pages: paginateReducer.pages,
  records: menuMissionReducer.list.records,
  permissions: menuReducer.permissions,
  menu: menuReducer.menu,
  all: menuMissionReducer.list.all,
  missions: missionsReducer.list,
})

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    { ...menuMissionActions, ...menuActions, ...missionsActions },
    dispatch
  )

/*
 * Exports component connected to redux as default
 */
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  translate(null, { wait: true, translateFuncName: 'translate' })(
    MenuMissionContainer
  )
)

MenuMissionContainer.propTypes = {
  all: PropTypes.array.isRequired,
  auth: PropTypes.object.isRequired,
  count: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
  deleteProfile: PropTypes.func.isRequired,
  exportCsv: PropTypes.func.isRequired,
  fetch: PropTypes.func.isRequired,
  fetchMissions: PropTypes.func.isRequired,
  fetchPermissions: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  menu: PropTypes.object.isRequired,
  missions: PropTypes.array.isRequired,
  pages: PropTypes.number.isRequired,
  permissions: PropTypes.array.isRequired,
  records: PropTypes.array.isRequired,
  save: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  updatePermissions: PropTypes.func.isRequired,
  updateProfile: PropTypes.func.isRequired,
}

export { MenuMissionContainer as MenuMission }
